import { DataSource } from '@angular/cdk/collections';
import { MatSort, SortDirection } from '@angular/material/sort';
import { merge, Observable, of, Subject, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';

export type SortAccessors<T> = Record<string, (item: T) => string | number>;

/** Returns `items` itself (same reference) when there is nothing to sort by. */
export function sortByAccessor<T>(
    items: T[],
    active: string | null | undefined,
    direction: SortDirection,
    accessors: SortAccessors<T>
): T[] {
    const accessor = active ? accessors[active] : undefined;
    if (!accessor || direction === '') {
        return items;
    }
    const isAsc = direction === 'asc';
    return [...items].sort((a, b) => {
        const left = accessor(a);
        const right = accessor(b);
        if (left === right) return 0;
        return (left < right ? -1 : 1) * (isAsc ? 1 : -1);
    });
}

/**
 * Client-side sortable data source for mat-table. Instance is created once and
 * never swapped: setData/refresh push through renderChange so the table
 * re-renders in place instead of disconnecting/reconnecting.
 *
 * `sort` is a setter because CdkTable subscribes to connect() before the
 * component's ngAfterViewInit assigns the real MatSort, so sortChange can't be
 * captured once at subscribe time.
 *
 * Subclasses customize via `prepare` (row transforms that must happen before
 * sorting, e.g. derived totals) and `getSortAccessors`.
 */
export class SortableTableDataSource<T> extends DataSource<T> {
    data: T[];
    private _sort: MatSort | undefined;
    private sortSubscription: Subscription | undefined;
    private renderChange = new Subject<void>();

    constructor(data: T[] = [], private accessors: SortAccessors<T> = {}) {
        super();
        this.data = data;
    }

    get sort(): MatSort | undefined {
        return this._sort;
    }

    set sort(sort: MatSort | undefined) {
        this._sort = sort;
        this.sortSubscription?.unsubscribe();
        this.sortSubscription = sort?.sortChange.subscribe(() => this.refresh());
        this.refresh();
    }

    connect(): Observable<T[]> {
        return merge(of(this.data), this.renderChange).pipe(map(() => this.getRenderedData()));
    }

    disconnect(): void {
        this.sortSubscription?.unsubscribe();
        this.renderChange.complete();
    }

    setData(data: T[]): void {
        this.data = data;
        this.refresh();
    }

    setSortAccessors(accessors: SortAccessors<T>): void {
        this.accessors = accessors;
        this.refresh();
    }

    refresh(): void {
        this.renderChange.next();
    }

    getRenderedData(): T[] {
        return sortByAccessor(this.prepare(this.data), this.sort?.active, this.sort?.direction ?? '', this.getSortAccessors());
    }

    protected prepare(data: T[]): T[] {
        return data;
    }

    protected getSortAccessors(): SortAccessors<T> {
        return this.accessors;
    }
}
